import { useCallback, useEffect, useRef, useState } from "react";
import { useTheme } from "../../contexts/ThemeContext";
import { isEmbeddedFrame, isTestEnvironment } from "../../utils/environment";
import { KEYBOARD_MESSAGE, KEY_MESSAGE } from "../../utils/uatTouchEmulation";
import UatVirtualKeyboard from "./UatVirtualKeyboard";

const MESSAGE_TYPE = "relinq-uat-viewport";
const THEME_STORAGE_KEY = "relinq.theme";
const FRAME_MARGIN = 16;

// Tamanhos em px CSS: iPhone 14/15 e iPad (retrato). A altura é limitada à
// janela real; a largura, que decide os breakpoints, fica a do aparelho.
const DEVICES = {
  mobile: { width: 390, height: 844, keyboard: 290, label: "Celular" },
  tablet: { width: 768, height: 1024, keyboard: 330, label: "Tablet" },
};

const isEditableTarget = (target) => {
  if (!target || typeof target !== "object") return false;
  const tag = String(target.tagName || "").toUpperCase();
  return (
    target.isContentEditable ||
    tag === "INPUT" ||
    tag === "TEXTAREA" ||
    tag === "SELECT"
  );
};

// L = celular, Shift + L = tablet. Devolve null para qualquer outra tecla.
const readShortcut = (event) => {
  if (event.key?.toLowerCase() !== "l") return null;
  if (event.ctrlKey || event.metaKey || event.altKey) return null;
  if (event.repeat || event.isComposing || event.defaultPrevented) return null;
  if (isEditableTarget(event.target)) return null;
  return event.shiftKey ? "tablet" : "mobile";
};

const PhoneIcon = () => (
  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" aria-hidden="true">
    <rect x="7" y="2.5" width="10" height="19" rx="2.4" stroke="currentColor" strokeWidth="1.8" />
    <path d="M10.5 5.2h3" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
    <circle cx="12" cy="18.6" r="0.9" fill="currentColor" />
  </svg>
);

const TabletIcon = () => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" aria-hidden="true">
    <rect x="4" y="2.5" width="16" height="19" rx="2.4" stroke="currentColor" strokeWidth="1.8" />
    <circle cx="12" cy="18.6" r="0.9" fill="currentColor" />
  </svg>
);

/**
 * Atalho SÓ DE TESTE para conferir a responsividade sem aparelho: a tecla L
 * mostra o sistema do tamanho de um celular e Shift + L do tamanho de um
 * tablet; repetir o mesmo atalho volta à janela inteira. Só liga em UAT e no
 * desenvolvimento local (`utils/environment.js`); em produção o componente não
 * registra ouvinte nem desenha nada.
 *
 * O navegador não deixa a página redimensionar a própria janela, e os
 * breakpoints do Tailwind olham a largura da janela, não a de um bloco. Por
 * isso a simulação abre o mesmo endereço num iframe com a largura do aparelho:
 * lá dentro o CSS responsivo se comporta como num celular de verdade. O iframe
 * (mesma origem, já logado) avisa por `postMessage` quando L é apertado dentro
 * dele, já que o teclado vai para o documento em foco.
 */
export default function UatViewportShortcut() {
  const enabled = isTestEnvironment();
  const insideFrame = enabled && isEmbeddedFrame();
  const { theme, setTheme } = useTheme();
  const [session, setSession] = useState(null); // { mode, src } ou null
  const [keyboard, setKeyboard] = useState(null); // { layout } ou null
  const sessionRef = useRef(null);
  const iframeRef = useRef(null);
  const [viewport, setViewport] = useState(() => ({
    width: window.innerWidth,
    height: window.innerHeight,
  }));
  sessionRef.current = session;

  // Ao sair, a tela principal assume onde o iframe foi parar (rota) e o tema
  // escolhido lá dentro; sem isso ela voltaria na página e no tema de antes.
  const closeSession = useCallback(() => {
    let nextPath = null;
    try {
      const location = iframeRef.current?.contentWindow?.location;
      if (location && location.origin === window.location.origin) {
        const path = `${location.pathname}${location.search}${location.hash}`;
        const current = `${window.location.pathname}${window.location.search}${window.location.hash}`;
        if (path !== current) nextPath = path;
      }
    } catch (error) {
      nextPath = null;
    }
    setSession(null);
    setKeyboard(null);
    if (nextPath) {
      window.location.assign(nextPath);
      return;
    }
    try {
      const stored = window.localStorage.getItem(THEME_STORAGE_KEY);
      if (stored && stored !== theme) setTheme(stored);
    } catch (error) {
      // ignore (private mode, etc.)
    }
  }, [theme, setTheme]);

  const request = useCallback(
    (mode) => {
      const current = sessionRef.current;
      if (current && current.mode === mode) {
        closeSession();
        return;
      }
      setSession({
        mode,
        src: current ? current.src : window.location.href,
      });
    },
    [closeSession],
  );

  useEffect(() => {
    if (!enabled) return undefined;
    const onKeyDown = (event) => {
      const mode = readShortcut(event);
      if (!mode) return;
      event.preventDefault();
      if (insideFrame) {
        window.parent.postMessage(
          { type: MESSAGE_TYPE, mode },
          window.location.origin,
        );
        return;
      }
      request(mode);
    };
    document.addEventListener("keydown", onKeyDown, true);
    return () => document.removeEventListener("keydown", onKeyDown, true);
  }, [enabled, insideFrame, request]);

  useEffect(() => {
    if (!enabled || insideFrame) return undefined;
    const onMessage = (event) => {
      if (event.origin !== window.location.origin) return;
      if (event.source !== iframeRef.current?.contentWindow) return;
      const data = event.data;
      if (!data) return;
      if (data.type === KEYBOARD_MESSAGE) {
        setKeyboard(data.open ? { layout: data.layout } : null);
        return;
      }
      if (data.type !== MESSAGE_TYPE || !DEVICES[data.mode]) return;
      request(data.mode);
    };
    window.addEventListener("message", onMessage);
    return () => window.removeEventListener("message", onMessage);
  }, [enabled, insideFrame, request]);

  useEffect(() => {
    if (!enabled || insideFrame || !session) return undefined;
    const onResize = () =>
      setViewport({ width: window.innerWidth, height: window.innerHeight });
    onResize();
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, [enabled, insideFrame, session]);

  // Tecla do teclado virtual: devolve o foco ao iframe (o clique na janela de
  // fora o tirou de lá) e manda a tecla para o campo que está em edição.
  const sendKey = (key) => {
    const frameWindow = iframeRef.current?.contentWindow;
    if (!frameWindow) return;
    frameWindow.focus();
    frameWindow.postMessage(
      { type: KEY_MESSAGE, key },
      window.location.origin,
    );
  };

  if (!enabled || insideFrame || !session) return null;

  const device = DEVICES[session.mode];
  const width = Math.min(device.width, viewport.width - FRAME_MARGIN);
  const height = Math.min(device.height, viewport.height - FRAME_MARGIN * 2);
  const Icon = session.mode === "mobile" ? PhoneIcon : TabletIcon;
  const keyboardHeight = Math.min(device.keyboard, Math.round(height * 0.5));

  // Cores fixas de propósito: o fundo da simulação é sempre escuro (paleta do
  // tema escuro) e o indicador fica sobre ele, igual nos dois temas.
  return (
    <>
      <div
        className="fixed inset-0 flex items-center justify-center"
        style={{ zIndex: 2147483646, background: "#1b1823" }}
        data-uat-viewport={session.mode}
      >
        <div
          style={{
            width,
            height,
            borderRadius: 24,
            overflow: "hidden",
            border: "6px solid #46415a",
            boxShadow: "0 20px 60px rgba(0, 0, 0, 0.45)",
            background: "#26232f",
            boxSizing: "content-box",
            display: "flex",
            flexDirection: "column",
          }}
        >
          <div style={{ flex: "1 1 0", minHeight: 0 }}>
            <iframe
              ref={iframeRef}
              title={`Simulação de tela: ${device.label}`}
              src={session.src}
              onLoad={() => {
                setKeyboard(null);
                iframeRef.current?.contentWindow?.focus();
              }}
              style={{ width: "100%", height: "100%", border: 0, display: "block" }}
            />
          </div>
          <UatVirtualKeyboard
            open={Boolean(keyboard)}
            layout={keyboard?.layout}
            height={keyboardHeight}
            onKey={sendKey}
          />
        </div>
      </div>
      <button
        type="button"
        onClick={closeSession}
        title={`Simulando ${device.label.toLowerCase()} — clique ou repita o atalho para sair`}
        aria-label={`Simulando ${device.label.toLowerCase()}. Sair da simulação`}
        className="fixed flex items-center gap-2.5 rounded-full"
        style={{
          zIndex: 2147483647,
          right: 16,
          bottom: 16,
          padding: "8px 14px",
          background: "#26232f",
          color: "#f4f2f8",
          border: "1px solid #46415a",
          boxShadow: "0 6px 20px rgba(0, 0, 0, 0.4)",
          cursor: "pointer",
        }}
      >
        <span className="rs-uat-dot" aria-hidden="true" />
        <Icon />
      </button>
    </>
  );
}
